import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import logoIcon from '../../imgs/1_trans.png';
import ImageWithFallback from './ImageWithFallback';
import { useQuery } from '@tanstack/react-query';
import { getCategories } from '../../api/api';

const Footer = () => {
  const { t, language } = useLanguage();

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
    staleTime: 600000, // 10 minutes
  });

  // Take the first 6-8 categories for the footer to keep it clean
  const footerCategories = Array.isArray(categories) ? categories.slice(0, 8) : [];

  return (
    <footer className="bg-background border-t mt-16 pb-12">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 group transition-all duration-300">
                <div className="h-10 w-10 shrink-0 bg-background dark:bg-slate-950 rounded-xl p-1 border border-border/40 shadow-xs flex items-center justify-center">
                    <ImageWithFallback src={logoIcon} alt="Ahlafot" className="w-full h-full object-contain transition-transform group-hover:scale-105" />
                </div>
                <h3 className="text-xl font-black bg-gradient-to-r from-primary via-purple-500 to-indigo-500 bg-clip-text text-transparent group-hover:scale-105 transition-transform uppercase tracking-tighter">
                    {t('brand')}
                </h3>
            </Link>
            <p className="text-muted-foreground mt-4 text-sm leading-relaxed">{t('tagline')}</p>
          </div>

          <div>
            <h4 className="font-black uppercase tracking-widest text-xs mb-6 text-foreground/80">{t('lookingFor', 'Looking For')}</h4>
            <ul className="space-y-3">
              {footerCategories.map((cat) => (
                <li key={cat._id}>
                  <Link 
                    to={`/browse?category=${cat._id}`} 
                    className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    <span className="w-1 h-1 bg-primary/40 rounded-full"></span>
                    {t('lookingFor', 'Looking for')} {cat[`label_${language}`] || cat.label_en || cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-black uppercase tracking-widest text-xs mb-6 text-foreground/80">{t('quickLinks')}</h4>
            <ul className="space-y-3">
              <li><Link to="/browse" className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium">{t('navBrowse')}</Link></li>
              <li><Link to="/create" className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium">{t('listItem')}</Link></li>
              <li><Link to="/my-items" className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium">{t('myItems')}</Link></li>
            </ul>
          </div>

           <div>
            <h4 className="font-black uppercase tracking-widest text-xs mb-6 text-foreground/80">{t('company')}</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium">{t('aboutUs')}</Link></li>
              <li><Link to="/faq" className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium">{t('faqTitle', 'FAQ')}</Link></li>
              <li><Link to="/careers" className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium">{t('careers')}</Link></li>
              <li><Link to="/press" className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium">{t('press')}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-black uppercase tracking-widest text-xs mb-6 text-foreground/80">{t('legal')}</h4>
            <ul className="space-y-3">
              <li><Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium">{t('termsOfService')}</Link></li>
              <li><Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors text-sm font-medium">{t('privacyPolicy')}</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-16 border-t border-border/50 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs font-bold text-muted-foreground uppercase tracking-widest">
          <div>&copy; {new Date().getFullYear()} {t('brand')}. {t('allRightsReserved', 'All rights reserved.')}</div>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-primary transition-colors">{t('privacyPolicy')}</Link>
            <Link to="/terms" className="hover:text-primary transition-colors">{t('termsOfService')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
